import * as Yup from 'yup';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  Container,
  Stack,
  Typography,
  Button,
  useTheme,
  Collapse,
  Slide,
  Divider,
  Fade,
  TextField,
  Radio,
  FormControlLabel,
  RadioGroup,
  FormControl,
  FormLabel,
  Tooltip
} from '@mui/material';
import React from 'react';
import { useFormik, Form, FormikProvider, Field } from 'formik';
import { LoadingButton } from '@mui/lab';
import { Icon } from '@iconify/react';
import checkmarkCircleFill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import questionMarkCircleFill from '@iconify/icons-eva/question-mark-circle-fill';
import _ from 'lodash';
import toast from 'react-hot-toast';
import { sentenceCase } from 'change-case';
// import { RadioGroup } from 'formik-material-ui';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import jwt from 'jsonwebtoken';

// services
import * as myLayananService from '../../../services/mylayanan';

import * as kategoriSEService from '../../../services/kategorise';
import QuestionsGenerator from '../../QuestionsGenerator';
import isEligible from '../../../utils/roleEligibleGetter';
import AddAttachment from './AddAttachment';
import { getAPIURL } from '../../../utils/envGetter';

ServicesDetailKSEQuestions.propTypes = {
  layanan: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  setQuestions: PropTypes.func.isRequired,
  selectedRequirement: PropTypes.object.isRequired,
  informations: PropTypes.object.isRequired,
  toggleUpdate: PropTypes.any,
  setToggleUpdate: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired
};

function ServicesDetailKSEQuestions({
  informations,
  layanan,
  questions,
  setQuestions,
  selectedRequirement,
  toggleUpdate,
  setToggleUpdate,
  user
}) {
  const [isKategoriSECreated, setIsKategoriSECreated] = React.useState(false);
  const questionsID = {};
  const questionsSchema = {};
  const valuesFromAPI = layanan?.meta?.kategori_se?.metadata?.answers;
  const dataDukungFromAPI = layanan?.meta?.kategori_se?.metadata?.data_dukung;
  const newDataDukung = {};
  // const [informations, setInformations] = React.useState([]);
  const [modeTandaTangan, setModeTandaTangan] = React.useState('tte');

  questions?.forEach((question) => {
    questionsID[question.id] = '';
    if (!_.isEmpty(valuesFromAPI)) questionsID[question.id] = valuesFromAPI[question.id];
    if (!_.isEmpty(dataDukungFromAPI)) {
      newDataDukung[question.id] = '';
      if (dataDukungFromAPI[question.id])
        newDataDukung[question.id] = dataDukungFromAPI[question.id];
    }

    if (question.required)
      questionsSchema[question.id] = Yup.string().required('Harap pilih opsi jawaban');
  });

  const getLevel = () => {
    let category = '';
    let level = 0;
    Object.entries(values).forEach((item) => {
      if (values[item[0]] === '1') level += 5;
      if (values[item[0]] === '2') level += 2;
      if (values[item[0]] === '3') level += 1;
    });

    if (level >= 36 && level <= 50) category = 'strategis';
    if (level >= 16 && level <= 35) category = 'tinggi';
    if (level >= 10 && level <= 15) category = 'rendah';

    return { category, level };
  };

  const saveData = async () => {
    try {
      const { category, level } = getLevel();

      const body = {
        data: {
          type: 'kategori_se',
          id: layanan?.attributes?.id_kategorisasi_se,
          attributes: {
            document_path: null,
            is_signed: null,
            status: 'saved',
            level: category
          },
          meta: {
            ...layanan?.meta?.kategori_se?.metadata,
            answers: values,
            data_dukung: dataDukungs,
            scores: { category, level }
          }
        }
      };

      toast
        .promise(kategoriSEService.updateKategoriSEData(body), {
          loading: 'Menyimpan data ...',
          error: <b>Maaf, terjadi kesalahan</b>,
          success: <b>Data berhasil disimpan</b>
        })
        .then(() => {
          setIsSaved(true);
          setToggleUpdate(!toggleUpdate);
        });
    } catch (error) {
      toast.error(error.message);
    }

    setSubmitting(false);
  };

  const QuestionSchema = Yup.object().shape(questionsSchema);
  const formik = useFormik({
    initialValues: {
      ...questionsID
    },
    enableReinitialize: true,
    validationSchema: QuestionSchema,
    onSubmit: () => {
      saveData();
    }
  });
  const {
    errors,
    touched,
    values,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setSubmitting,
    setFieldValue,
    setErrors
  } = formik;

  const [dataDukungs, setDataDukungs] = React.useState({});
  const [isSaved, setIsSaved] = React.useState(false);
  const [isSigned, setIsSigned] = React.useState(false);
  const [isVerified, setIsVerified] = React.useState(false);

  const startNewQuestionnaire = async () => {
    try {
      const body = {
        data: {
          id: layanan.id,
          type: 'id_mylayanan'
        }
      };

      toast
        .promise(myLayananService.startNewQuestionnaire(body), {
          loading: 'Mempersiapkan kuesioner...',
          success: <b>Silakan memulai kuesioner</b>,
          error: <b>Maaf, terjadi kesalahan.</b>
        })
        .then((result) => {
          if (result.data) {
            setIsKategoriSECreated(true);
            setToggleUpdate(!toggleUpdate);
          }
        });
    } catch (error) {
      toast.error(error.message);
    }
  };

  React.useEffect(() => {
    if (isKategoriSECreated) setDataDukungs({ ...dataDukungs, ...newDataDukung });
    if (layanan?.meta?.kategori_se?.metadata?.answers) setIsSaved(true);
    if (layanan?.meta?.kategori_se?.is_signed && layanan?.meta?.kategori_se?.document_path)
      setIsSigned(true);
  }, [isKategoriSECreated]);

  React.useEffect(() => {
    if (layanan?.meta?.kategori_se?.id_versi_kategori_se) setIsKategoriSECreated(true);
  }, []);

  const theme = useTheme();
  const navigate = useNavigate();

  const previewDocument = () => {
    const accessToken = jwt.sign(
      {
        requestor: layanan?.attributes?.requestor,
        id_requirement: selectedRequirement.id,
        modeTandaTangan,
        selectedRequirement
      },
      process.env.REACT_APP_SECRET_KEY,
      {
        expiresIn: '24h'
      }
    );

    navigate(`/pdf/${layanan.id}/${accessToken}`);
  };

  const openDocument = () => {
    const origin = getAPIURL();
    const path = `${origin}/pdf?path=${layanan.meta.kategori_se.document_path
      .replace('.', '')
      .substring(1)}&id=${layanan.meta.kategori_se.id}&filename=Formulir Kategori SE`;
    window.open(path, '_blank');
  };
  return (
    <Stack direction="column" spacing={2} divider={<Divider orientation="horizontal" flexItem />}>
      <Box pt={3}>
        <Stack direction="column" spacing={2}>
          <Typography variant="h4">{selectedRequirement.attributes.label}</Typography>
          <Stack
            spacing={1}
            direction={{ xs: 'column', sm: 'row' }}
            justifyItems="flex-start"
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon
                icon={isSaved ? checkmarkCircleFill : closeCircleFill}
                color={isSaved ? theme.palette.primary.main : theme.palette.grey[400]}
              />
              <Typography>Simpan Data</Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon
                icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                color={isSigned ? theme.palette.primary.main : theme.palette.grey[400]}
              />
              <Typography>Tanda Tangan (TTE/Manual)</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {_.isEmpty(layanan?.meta?.kategori_se?.id_versi_kategori_se) && !isKategoriSECreated ? (
        <Stack direction="column">
          <Box>
            {isEligible('kategori_se') ? (
              <Button onClick={() => startNewQuestionnaire()} variant="contained">
                Mulai Kuesioner Baru
              </Button>
            ) : (
              <Typography>Belum ada data</Typography>
            )}
          </Box>
        </Stack>
      ) : (
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack
              spacing={2}
              direction={{ sm: 'column', xs: 'column', md: 'column', lg: 'row' }}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Fade in="true">
                <Stack spacing={2} direction="column" flex={6}>
                  {!_.isEmpty(questions) &&
                    questions.map((item) => (
                      <QuestionsGenerator
                        item={item}
                        values={values}
                        setFieldValue={setFieldValue}
                        getFieldProps={getFieldProps}
                        errors={errors}
                        touched={touched}
                        isSubmitting={isSubmitting}
                        handleSubmit={handleSubmit}
                        setSubmitting={setSubmitting}
                        dataDukungs={dataDukungs}
                        setDataDukungs={setDataDukungs}
                      />
                    ))}

                  {!isSigned ? (
                    <>
                      {isEligible('kategori_se') && (
                        <LoadingButton
                          fullWidth
                          size="large"
                          type="submit"
                          variant="contained"
                          loading={isSubmitting}
                        >
                          {isSaved ? 'Update Data' : 'Simpan Data'}
                        </LoadingButton>
                      )}
                      {isSaved && isEligible('kategori_se') && (
                        <Stack direction="column">
                          {/* <FormControl>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <FormLabel>Pilih mode tanda tangan</FormLabel>
                              <Tooltip
                                title="Dokumen permohonan harus ditandatangani terlebih dahulu oleh Kepala Dinas/Instansi baik secara elektronik maupun manual. TTE (Tanda Tangan Elektronik) dapat memudahkan Anda melakukan proses tanda tangan, tanpa harus mengunduh dokumen dan mengunggah dokumen yang telah ditandatangani."
                                arrow
                              >
                                <Icon
                                  color={theme.palette.grey[500]}
                                  icon={questionMarkCircleFill}
                                />
                              </Tooltip>
                            </Stack>

                            <RadioGroup
                              sx={{ padding: 1 }}
                              aria-labelledby="Pilih mode tanda tangan"
                              value={modeTandaTangan}
                              name="modeTandaTangan"
                              onChange={(event) => {
                                setModeTandaTangan(event.target.value);
                              }}
                            >
                              <FormControlLabel
                                value="tte"
                                control={<Radio />}
                                label="Tanda Tangan Elektronik (TTE) - Direkomendasikan"
                              />
                              <FormControlLabel
                                value="manual"
                                control={<Radio />}
                                label="Tanda Tangan Manual"
                              />
                            </RadioGroup>
                          </FormControl> */}
                          <Divider sx={{ marginBottom: 3 }} />
                          <LoadingButton
                            onClick={previewDocument}
                            fullWidth
                            size="large"
                            variant="contained"
                          >
                            Submit Tanda Tangan
                          </LoadingButton>
                        </Stack>
                      )}
                    </>
                  ) : (
                    <>
                      <Stack direction="row" pl={2} spacing={2} alignItems="center">
                        <Icon
                          icon={isSigned ? checkmarkCircleFill : closeCircleFill}
                          width={32}
                          height={32}
                          color={isSigned ? theme.palette.primary.main : theme.palette.error.main}
                        />
                        <Typography variant="body2">
                          {isSigned ? 'Sudah ditandatangani' : 'Belum ditandatangani'}
                        </Typography>
                        <Button onClick={openDocument} disabled={!isSigned} variant="outlined">
                          Lihat dokumen
                        </Button>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Fade>
              <Box sx={{ flex: 3 }}>
                <Stack direction="column" spacing={2}>
                  <Typography variant="h5">Informasi</Typography>
                  {!_.isEmpty(informations) &&
                    informations?.map((item, index) => (
                      <Box key={item.id}>
                        <Stack direction="column" spacing={2}>
                          <Stack direction="row" spacing={2}>
                            <Box>
                              <Typography variant="body2">{index + 1}.</Typography>
                            </Box>
                            <Box sx={{ flex: 10 }}>
                              <Typography variant="body2">{item.label}</Typography>
                            </Box>
                          </Stack>
                          {item.has_children && (
                            <Stack direction="column" spacing={1}>
                              {item.children.map((child) => (
                                <>
                                  {child.type === 'list' && (
                                    <>
                                      {child.lists.map((subChild) => (
                                        <Stack
                                          direction="row"
                                          spacing={2}
                                          divider={<Divider orientation="vertical" flexItem />}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ maxWidth: 80, minWidth: 80 }}
                                          >
                                            {subChild.key}
                                          </Typography>
                                          {Array.isArray(subChild.value) ? (
                                            <Stack
                                              direction="row"
                                              spacing={2}
                                              divider={<Divider orientation="vertical" flexItem />}
                                            >
                                              {subChild.value.map((subChildValue) => (
                                                <Typography
                                                  sx={{ maxWidth: 80, minWidth: 80 }}
                                                  variant="body2"
                                                >
                                                  {subChildValue}
                                                </Typography>
                                              ))}
                                            </Stack>
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              sx={{ maxWidth: 80, minWidth: 80 }}
                                            >
                                              {subChild.value}
                                            </Typography>
                                          )}
                                        </Stack>
                                      ))}
                                    </>
                                  )}
                                </>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  <Stack direction="column" spacing={2}>
                    <Typography variant="h5">Hasil Kategorisasi SE</Typography>
                    <Typography variant="subtitle">
                      Total Nilai: <b>{getLevel().level}</b>
                    </Typography>
                    <Typography variant="subtitle">
                      Kategori: <b>{sentenceCase(getLevel().category)}</b>
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="column" mt={2}>
                  <AddAttachment
                    idRequirement={layanan?.attributes?.id_kategorisasi_se}
                    user={user}
                    type="kategori_se"
                  />
                </Stack>
              </Box>
            </Stack>
          </Form>
        </FormikProvider>
      )}
    </Stack>
  );
}

export default ServicesDetailKSEQuestions;
